/**
 * Chart orientation modes and the small pure helpers around them.
 *
 * Three ways to hold the chart, the standard RYA set:
 *   - north:  the chart is fixed, north at the top (the plotting default).
 *   - head:   the chart rotates so our heading points up - where the bow is
 *             pointing, which matches the view over the front of the boat and
 *             suits close-quarters work.
 *   - course: the chart rotates so our course over the ground (COG) points up -
 *             where we're actually going once tide and leeway have had their say.
 * Head and course differ whenever tide or leeway push us off our heading, which
 * is exactly the moment the difference is worth seeing.
 */
export type OrientationMode = 'north' | 'head' | 'course';

/** Cycle order for the on-chart button: north -> head -> course -> north. */
export const ORIENTATION_MODES: readonly OrientationMode[] = ['north', 'head', 'course'];

/** The next mode when the on-chart orientation button is tapped (wraps round). */
export function nextOrientation(mode: OrientationMode): OrientationMode {
  const i = ORIENTATION_MODES.indexOf(mode);
  return ORIENTATION_MODES[(i + 1) % ORIENTATION_MODES.length];
}

/** Full label for the menu selector and the toast. */
export function orientationLabel(mode: OrientationMode): string {
  switch (mode) {
    case 'north':
      return 'North up';
    case 'head':
      return 'Head up';
    case 'course':
      return 'Course up';
  }
}

/** Short badge for the on-chart button (N / H / C with an up arrow). */
export function orientationBadge(mode: OrientationMode): string {
  switch (mode) {
    case 'north':
      return 'N↑';
    case 'head':
      return 'H↑';
    case 'course':
      return 'C↑';
  }
}

/**
 * The compass bearing (degrees) the map should point "up", given the mode, the
 * live ownship, and the manual/gesture rotation used in north-up. Pure, so the
 * map effect and the tests read the exact same rule.
 *   - north:  the manual rotation (0 = true north; a gesture can still turn it).
 *   - head:   heading, falling back to COG while moving, else 0.
 *   - course: COG while moving, then heading, else 0 (a stopped boat has no COG,
 *             so we hold the heading rather than snap to north).
 */
export function orientationBearingDeg(
  mode: OrientationMode,
  os: { heading: number; cog: number; sog: number },
  rotation: number,
): number {
  if (mode === 'north') return rotation;
  if (mode === 'head') return os.heading || (os.sog > 0 ? os.cog : 0);
  const cog = os.sog > 0 ? os.cog : 0;
  return cog || os.heading || 0;
}
